import React from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import { AuditChangeRows } from "../../../components/admin/AuditLogVisual";
import { auditActionLabel } from "../../../utils/auditDisplay";
import { sectionCardClass, sectionTitleClass } from "./tenantDetailStyles";
import { formatDateTime } from "../../../utils/datetime";

export default function TenantHistoryBlock({ events }) {
  const { theme } = useTheme();
  const isLight = theme === "light";
  const cardClass = isLight
    ? "tenant-history-card mb-0 overflow-hidden rounded-[12px] border border-slate-200 bg-white shadow-sm"
    : `${sectionCardClass} tenant-history-card`;
  const headerBorder = isLight ? "border-slate-200" : "border-[#1c2035]";
  const titleCls = isLight
    ? "text-[11px] font-medium uppercase tracking-[0.5px] text-slate-600"
    : sectionTitleClass;
  const countCls = isLight ? "text-[10px] text-slate-500" : "text-[10px] text-[#4a5070]";
  const emptyCls = isLight ? "m-0 text-[11px] text-slate-500" : "m-0 text-[11px] text-[#4a5070]";
  const rowBorder = isLight ? "border-slate-200" : "border-[#1c2035]";
  const summaryCls = isLight
    ? "mb-[2px] text-[11px] font-medium text-slate-900"
    : "mb-[2px] text-[11px] font-medium text-[#edf0f7]";
  const labelCls = isLight
    ? "text-[9px] font-semibold uppercase tracking-wide text-slate-500"
    : "text-[9px] font-semibold uppercase tracking-wide text-[#4a5070]";
  const metaCls = isLight ? "mt-2 text-[10px] text-slate-500" : "mt-2 text-[10px] text-[#4a5070]";
  const authorCls = isLight ? "text-amber-700" : "text-[#f5a623]";

  return (
    <div className={cardClass} data-admin-tenant-history="1">
      <div className={`tenant-history-header flex items-center justify-between border-b ${headerBorder} px-[16px] py-[12px]`}>
        <div className={titleCls}>Änderungsprotokoll</div>
        <span className={countCls}>{events.length}</span>
      </div>
      <div className="tenant-history-scroll max-h-[22rem] min-h-0 overflow-y-auto overflow-x-hidden px-[16px] py-[14px]">
        {!events.length ? (
          <p className={emptyCls}>Noch kein Verlauf</p>
        ) : (
          <ul className="m-0 list-none p-0">
            {events.map((ev) => {
              const showDiff =
                ev.action_type === "tenant_updated" &&
                ev.field_name &&
                (ev.old_value != null || ev.new_value != null);
              const isAudit = String(ev.id || "").startsWith("audit-");
              const auditChanges = isAudit && Array.isArray(ev.audit_changes) ? ev.audit_changes : [];
              const auditWrap = isLight ? (
                <AuditChangeRows changes={auditChanges} listClassName="!mt-2" />
              ) : (
                <div className="dark">
                  <AuditChangeRows changes={auditChanges} listClassName="!mt-2" />
                </div>
              );
              return (
                <li key={ev.id} className={`border-b py-[10px] last:border-b-0 ${rowBorder}`}>
                  <div className={summaryCls}>{ev.summary}</div>
                  {auditChanges.length > 0 ? auditWrap : null}
                  {!auditChanges.length && showDiff ? (
                    <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <div>
                        <div className={labelCls}>Alt</div>
                        <div
                          className={
                            isLight
                              ? "mt-0.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[12px] text-amber-950"
                              : "mt-0.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-[12px] text-amber-100"
                          }
                        >
                          {ev.old_value ?? "—"}
                        </div>
                      </div>
                      <div>
                        <div className={labelCls}>Neu</div>
                        <div
                          className={
                            isLight
                              ? "mt-0.5 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-[12px] text-emerald-950"
                              : "mt-0.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1.5 text-[12px] text-emerald-100"
                          }
                        >
                          {ev.new_value ?? "—"}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className={metaCls}>
                    {formatDateTime(ev.created_at)}
                    {isAudit && ev.audit_action != null && String(ev.audit_action) !== ""
                      ? ` · ${auditActionLabel(ev.audit_action)}`
                      : ""}
                    {" · "}
                    <span className={authorCls}>{ev.author_name || "—"}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
